import type {
  Department,
  ListOrgFunctionsItem,
  ProcessListRow,
  SystemWithCapacity,
  UnresolvedDepartment,
  User
} from '@bliks-io/contracts';
import type { ParsedOrgFunctionRow, ParsedSystemRow } from '@/lib/excel';
import type { InitializeProcessDraft } from '@/components/bulk-upload';
import type { ParsedDepartment, ParsedUser } from './excelParsers';

/**
 * Only wizard-imported rows that were never invited or activated are
 * replaceable/editable (ADR 0011) — mirrors the backend's
 * REPLACEABLE_USER_FILTER exactly, so the wizard never seeds a row the
 * backend would reject. Everything else (superadmin-precreated, invited or
 * activated users) survives a re-import and is shown separately as read-only.
 * Both backend branches are mirrored: the `createdVia: 'initial-setup'` stamp,
 * and the legacy heuristic for rows imported before that provenance existed
 * (no `createdVia`, but department AND Stelle pre-linked while still inactive).
 */
export const isReplaceableWizardUser = (member: User): boolean =>
  member.status === 'inactive' &&
  member.invitedAt === undefined &&
  member.activatedAt === undefined &&
  (member.createdVia === 'initial-setup' ||
    (member.createdVia === undefined && !!member.department && !!member.mainRole));

/**
 * Members whose identity the draft table can NOT edit — before the first
 * import that is everyone, afterwards only the non-replaceable rest (the
 * replaceable imports become fully editable seeds). These render as
 * locked-identity rows whose department/role commits via the placement PATCH
 * (see utils/protectedRows) — the add-users pipeline would reject them.
 */
export function nonEditableMembers(members: User[], usersDone: boolean): User[] {
  return members.filter((member) => !usersDone || !isReplaceableWizardUser(member));
}

/**
 * Maps the already-imported departments (materialized ones plus unresolved rows still
 * awaiting manager resolution) back into the wizard's draft-row shape, so
 * Bearbeiten can seed the editable table with exactly what was imported.
 * `entityId` carries the entity's `_id` so Weiter can send edits as in-place
 * updates (ADR 0011, second amendment).
 */
export function departmentsToDrafts(
  imported: Array<Department | UnresolvedDepartment>
): ParsedDepartment[] {
  const abbreviationById = new Map(imported.map((dept) => [dept._id, dept.abbreviation]));
  return imported.map((dept, index) => {
    const pathSegments = dept.path?.split('#') ?? [];
    const parentId = pathSegments[pathSegments.length - 2];
    return {
      data: {
        name: dept.name,
        abbreviation: dept.abbreviation,
        description: dept.description,
        parentAbbr: parentId ? abbreviationById.get(parentId) : undefined,
        managerEmail:
          ('managerEmail' in dept && dept.managerEmail) ||
          ('manager' in dept && dept.manager?.email) ||
          ''
      },
      isValid: true,
      rowNumber: index + 1,
      entityId: dept._id
    };
  });
}

/**
 * Maps existing OrgFunctions into the Funktionen/Teams draft-row shape.
 * Single source for the step's seeded table AND the submit-time unchanged
 * check — both must see identical rows, or an untouched seeded row could be
 * sent as a destructive update. `entityId` drives the in-place edit on
 * Weiter: editing a seeded row updates that OrgFunction by id, so a rename
 * flows back to every user whose Stelle is this function (backward sync).
 */
export function orgFunctionsToDrafts(imported: ListOrgFunctionsItem[]): ParsedOrgFunctionRow[] {
  return imported.map((fn, index) => ({
    data: {
      name: fn.name,
      description: fn.description,
      // Reference the department by stable id so a rename auto-follows.
      department: fn.department?._id,
      leadEmail: fn.lead?.email,
      email: fn.email
    },
    isValid: true,
    rowNumber: index + 1,
    entityId: fn._id
  }));
}

/**
 * Maps org members back into the users step's draft-row shape. The Stelle's
 * Beschreibung lives on the role OrgFunction, not the user, so it is rejoined
 * by `mainRole._id` from `roleDescriptionById` — otherwise re-entering the step
 * would blank a Stellenbeschreibung the user already entered.
 */
export function usersToDrafts(
  members: User[],
  roleDescriptionById?: Map<string, string | undefined>
): ParsedUser[] {
  return members.map((member, index) => ({
    data: {
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      // Reference the department by stable id — a later Abkürzung rename then
      // auto-follows in the seeded table (see @/lib/department-ref).
      department: member.department?._id ?? '',
      role: member.mainRole?.name ?? '',
      roleDescription: member.mainRole ? roleDescriptionById?.get(member.mainRole._id) : undefined
    },
    isValid: true,
    rowNumber: index + 1,
    entityId: member._id
  }));
}

/**
 * Maps the already-imported systems back into the systems step's draft-row
 * shape. During setup every system stems from the wizard (the org is still
 * gated), so the whole list seeds the editable table.
 */
export function systemsToDrafts(systems: SystemWithCapacity[]): ParsedSystemRow[] {
  return systems.map((system, index) => ({
    data: {
      name: system.name,
      description: system.description,
      category: system.category,
      url: system.url,
      licenceSeats: system.licenceSeats,
      ownerEmail: typeof system.systemOwner === 'object' ? (system.systemOwner.email ?? '') : ''
    },
    isValid: true,
    rowNumber: index + 1,
    entityId: system._id
  }));
}

/**
 * Maps the already-imported processes back into the processes step's
 * draft-row shape. Department is referenced by stable id (matching the
 * import-boundary resolution in ProcessesStep); the wizard-only client id
 * reuses the entity id.
 */
export function processesToDrafts(processes: ProcessListRow[]): InitializeProcessDraft[] {
  return processes.map((process, index) => ({
    id: process._id,
    data: {
      name: process.name,
      description: process.description ?? '',
      department: process.department?._id ?? '',
      ownerEmail: process.owner?.email ?? '',
      accountableEmail: process.accountable?.email ?? '',
      category: process.category
    },
    isValid: true,
    rowNumber: index + 1,
    entityId: process._id
  }));
}
